import type { CSSProperties } from "react";

/**
 * A six-sided die, in CSS.
 *
 * It shows a number the server already decided — every roll in this app comes
 * from a seed held on the server and redacted from the client, so there is
 * nothing to simulate here and nothing a clever child could bias by tampering
 * with the page. The cube exists because a number appearing out of nowhere
 * does not feel like a throw, and a throw is half of why dice games are fun.
 *
 * `aria-hidden`, always: the number it landed on is written next to it in real
 * text, so a screen reader gets the fact without the theatre.
 */

/** How far to turn the cube so a given face is looking at you. */
const FACING: Record<number, { rx: number; ry: number }> = {
	1: { rx: 0, ry: 0 },
	2: { rx: 0, ry: -90 },
	3: { rx: -90, ry: 0 },
	4: { rx: 90, ry: 0 },
	5: { rx: 0, ry: 90 },
	6: { rx: 0, ry: 180 },
};

/** Where each face sits on the cube, and which pips it carries. */
const FACES: { value: number; transform: string }[] = [
	{ value: 1, transform: "translateZ(var(--half))" },
	{ value: 6, transform: "rotateY(180deg) translateZ(var(--half))" },
	{ value: 2, transform: "rotateY(90deg) translateZ(var(--half))" },
	{ value: 5, transform: "rotateY(-90deg) translateZ(var(--half))" },
	{ value: 3, transform: "rotateX(90deg) translateZ(var(--half))" },
	{ value: 4, transform: "rotateX(-90deg) translateZ(var(--half))" },
];

/**
 * Pip positions in a 3×3 grid, the way they are printed on a real die. Given
 * as grid cells so the faces stay square at any size.
 */
const PIPS: Record<number, [number, number][]> = {
	1: [[2, 2]],
	2: [
		[1, 1],
		[3, 3],
	],
	3: [
		[1, 1],
		[2, 2],
		[3, 3],
	],
	4: [
		[1, 1],
		[1, 3],
		[3, 1],
		[3, 3],
	],
	5: [
		[1, 1],
		[1, 3],
		[2, 2],
		[3, 1],
		[3, 3],
	],
	6: [
		[1, 1],
		[1, 3],
		[2, 1],
		[2, 3],
		[3, 1],
		[3, 3],
	],
};

interface Props {
	/** The face to show, 1–6. */
	value: number;
	/**
	 * Which throw this is. Changing it re-runs the tumble, which is the only
	 * way to replay it when somebody rolls the same number twice in a row.
	 */
	roll?: number;
	/** Edge length. Defaults to 3.5rem. */
	size?: string;
	className?: string;
}

export function Die({ value, roll = 0, size, className = "" }: Props) {
	const face = FACING[value] ? value : 1;
	const { rx, ry } = FACING[face];

	return (
		// The perspective lives on the parent, so the cube keeps its own
		// transform free for the tumble.
		<span
			aria-hidden="true"
			className={`inline-block [perspective:600px] ${className}`}
			style={size ? ({ "--die-size": size } as CSSProperties) : undefined}
		>
			<span
				key={roll}
				className="die die-rolling"
				style={
					{
						"--die-rx": `${rx}deg`,
						"--die-ry": `${ry}deg`,
						"--half": "calc(var(--die-size, 3.5rem) / 2)",
					} as CSSProperties
				}
			>
				{FACES.map((entry) => (
					<span key={entry.value} className="die-face" style={{ transform: entry.transform }}>
						{PIPS[entry.value].map(([row, column]) => (
							<span
								key={`${row}-${column}`}
								className="die-pip"
								style={{ gridRow: row, gridColumn: column }}
							/>
						))}
					</span>
				))}
			</span>
		</span>
	);
}
